import { useState, useEffect } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, Badge } from '../components/ui'
import { useCompany } from '../hooks/useCompany'
import { useAuth } from '../hooks/useAuth'

interface Shift {
  id: string
  rule_id: string | null
  date: string
  start_time: string
  end_time: string
  location_id: string | null
  location_name: string
  created_at: number
  assignments: Array<{
    user_id: string
    user_name: string
    user_email: string
  }>
}

interface ShiftStats {
  totalShifts: number
  totalHours: number
  assignedShifts: number
  unassignedShifts: number
}

export default function Schedule() {
  const { currentCompany } = useCompany()
  const { } = useAuth()
  const [shifts, setShifts] = useState<Shift[]>([])
  const [stats, setStats] = useState<ShiftStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null)
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date()
    const sunday = new Date(today)
    sunday.setDate(today.getDate() - today.getDay())
    return sunday
  })

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  // Calculate week dates
  const getWeekDates = () => {
    const dates = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart)
      date.setDate(currentWeekStart.getDate() + i)
      dates.push(date)
    }
    return dates
  }

  const weekDates = getWeekDates()
  const startDate = currentWeekStart.toISOString().split('T')[0]
  const endDate = (() => {
    const end = new Date(currentWeekStart)
    end.setDate(currentWeekStart.getDate() + 6)
    return end.toISOString().split('T')[0]
  })()

  // Fetch teams for current company
  useEffect(() => {
    const fetchTeamsAndSetCurrent = async () => {
      if (!currentCompany?.id) {
        setCurrentTeamId(null)
        setLoading(false)
        return
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/v1/teams?company_id=${currentCompany.id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch teams')
        }

        const data = await response.json()
        const teams = data.teams || []
        
        // Find a team where the user is a member
        const userTeam = teams.find((team: any) => team.user_role !== null)
        
        if (userTeam) {
          setCurrentTeamId(userTeam.id)
        } else if (teams.length > 0) {
          setCurrentTeamId(teams[0].id)
        } else {
          setCurrentTeamId(null)
          setError('No teams found in this company')
        }
      } catch (err) {
        console.error('Failed to fetch teams:', err)
        setCurrentTeamId(null)
        setError('Failed to load teams')
      }
    }

    fetchTeamsAndSetCurrent()
  }, [currentCompany?.id])

  // Fetch shifts and stats
  useEffect(() => {
    if (!currentTeamId) {
      return
    }

    fetchShifts()
    fetchStats()
  }, [currentTeamId, startDate, endDate])

  const fetchShifts = async () => {
    if (!currentTeamId) return

    try {
      setLoading(true)
      setError(null)

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/shifts?team_id=${currentTeamId}&start_date=${startDate}&end_date=${endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch shifts')
      }

      const data = await response.json()
      setShifts(data.shifts || [])
    } catch (err) {
      console.error('Failed to fetch shifts:', err)
      setError(err instanceof Error ? err.message : 'Failed to load shifts')
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    if (!currentTeamId) return

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/v1/shifts/stats?team_id=${currentTeamId}&start_date=${startDate}&end_date=${endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        setStats(data.stats)
      }
    } catch (err) {
      console.error('Failed to fetch shift stats:', err)
    }
  }

  const goToPreviousWeek = () => {
    const newWeek = new Date(currentWeekStart)
    newWeek.setDate(currentWeekStart.getDate() - 7)
    setCurrentWeekStart(newWeek)
  }

  const goToNextWeek = () => {
    const newWeek = new Date(currentWeekStart)
    newWeek.setDate(currentWeekStart.getDate() + 7)
    setCurrentWeekStart(newWeek)
  }

  const getShiftsForDay = (dayIndex: number) => {
    const targetDate = weekDates[dayIndex].toISOString().split('T')[0]
    return shifts.filter(shift => shift.date === targetDate)
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${displayHour}:${minutes} ${ampm}`
  }

  const formatTimeRange = (startTime: string, endTime: string) => {
    return `${formatTime(startTime)} - ${formatTime(endTime)}`
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="grid grid-cols-7 gap-4">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-10 bg-gray-200 rounded"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load schedule</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => { fetchShifts(); fetchStats(); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!currentCompany) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <p className="text-gray-600">Please select a company to view the schedule.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 sm:px-0">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Schedule</h1>
            <p className="text-sm text-gray-600 mt-1">
              {weekDates[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - {weekDates[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex gap-3">
            <Button variant="secondary" onClick={goToPreviousWeek}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous Week
            </Button>
            <Button variant="secondary" onClick={goToNextWeek}>
              Next Week
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Button>
            <Button>Create Shift</Button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="grid grid-cols-7 gap-4">
          {days.map((day, index) => {
            const dayShifts = getShiftsForDay(index)
            const date = weekDates[index]
            return (
              <div key={day} className="space-y-2">
                <h3 className="text-sm font-medium text-gray-900 text-center p-2 bg-gray-100 rounded">
                  <div>{day}</div>
                  <div className="text-xs text-gray-600 font-normal">
                    {date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
                  </div>
                </h3>
                <div className="space-y-2">
                  {dayShifts.map((shift) => (
                    <Card key={shift.id} className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardContent className="p-3">
                        <p className="text-xs font-medium">
                          {formatTimeRange(shift.start_time, shift.end_time)}
                        </p>
                        {shift.assignments.length > 0 ? (
                          shift.assignments.map((assignment, idx) => (
                            <p key={idx} className="text-sm font-semibold mt-1">
                              {assignment.user_name}
                            </p>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 mt-1 italic">Unassigned</p>
                        )}
                        <Badge variant="secondary" className="mt-2 text-xs">
                          {shift.location_name}
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                  {dayShifts.length === 0 && (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-500">No shifts</p>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Shift Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Total Shifts</span>
                <span className="text-sm font-medium">{stats?.totalShifts || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Total Hours</span>
                <span className="text-sm font-medium">{stats?.totalHours || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Assigned</span>
                <span className="text-sm font-medium">{stats?.assignedShifts || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">Unassigned</span>
                <span className={`text-sm font-medium ${(stats?.unassignedShifts || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {stats?.unassignedShifts || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Schedule Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(stats?.unassignedShifts || 0) > 0 ? (
                <div className="p-2 bg-red-50 rounded">
                  <p className="text-sm font-medium text-red-800">Attention Needed</p>
                  <p className="text-xs text-red-700">
                    {stats?.unassignedShifts} shift{(stats?.unassignedShifts || 0) > 1 ? 's' : ''} need assignment
                  </p>
                </div>
              ) : (
                <div className="p-2 bg-green-50 rounded">
                  <p className="text-sm font-medium text-green-800">All Set</p>
                  <p className="text-xs text-green-700">All shifts are assigned</p>
                </div>
              )}
              {shifts.length === 0 && (
                <div className="p-2 bg-blue-50 rounded">
                  <p className="text-sm font-medium text-blue-800">No Schedule</p>
                  <p className="text-xs text-blue-700">No shifts scheduled for this week</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Button className="w-full" variant="secondary">Copy Last Week</Button>
              <Button className="w-full" variant="secondary">Auto-Schedule</Button>
              <Button className="w-full" variant="secondary">Publish Schedule</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}